// src/pages/Dashboard/components/SecondLevel.jsx
// STATUS: active | UPDATED: 2026-09-01
// LOG:
//   2026-09-01  New. What happens after somebody clears all thirteen.
//
// Jazz, having finished the whole thing, asked whether going back through again
// is how it should stay or whether there should be another advanced level.
//
// He is right that going round again is thin. But the advanced level does not
// need inventing, because it is already in his own method and the product was
// simply not saying so.
//
// Passing the knowledge check proves you know the step. Being signed off proves
// you can do it in front of a customer while somebody watches. Those are two
// different things and the second is the hard one. Jazz scored four out of four
// on all thirteen checks at the first attempt, which is exactly what you would
// expect from the man who wrote them, and has been signed off on none.
//
// So this is not a second curriculum. It is the product finally being honest
// that the first half is the easy half.
//
// WHY NOT JUST UNLOCK MORE CONTENT
//
// Because more content is what a training company sells when it has run out of
// ideas, and this one teaches that the floor is where it counts. A salesperson
// who has read everything twice and been watched never is not further along.

import { useEffect, useState } from 'react'
import { Box, HStack, VStack, Text, Icon, Button, Flex } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Award, ArrowRight } from 'lucide-react'
import { supabase } from '../../../lib/supabase'
import { STEPS } from '../../../content/steps'

export default function SecondLevel({ userId }) {
  const [state, setState] = useState({ loading: true, passed: 0, signed: 0, nextStep: null })

  useEffect(() => {
    if (!userId) return undefined
    let live = true

    Promise.all([
      supabase.from('step_progress').select('step_id, status').eq('user_id', userId),
      supabase.from('steps').select('id, number'),
    ]).then(([progressRes, stepsRes]) => {
      if (!live || progressRes.error || stepsRes.error) {
        if (live) setState((s) => ({ ...s, loading: false }))
        return
      }
      const numberById = {}
      for (const row of stepsRes.data || []) numberById[row.id] = row.number

      const rows = progressRes.data || []
      const passed = rows.filter((r) => r.status === 'check_passed' || r.status === 'signed_off')
      const signed = rows.filter((r) => r.status === 'signed_off')

      // The lowest numbered step that is known but not yet witnessed. Working
      // in order is how the process is taught, so it is how this suggests it.
      const awaiting = passed
        .filter((r) => r.status !== 'signed_off')
        .map((r) => numberById[r.step_id])
        .filter(Boolean)
        .sort((a, b) => a - b)

      setState({
        loading: false,
        passed: passed.length,
        signed: signed.length,
        nextStep: awaiting[0] || null,
      })
    })

    return () => {
      live = false
    }
  }, [userId])

  // Only for people who have actually cleared everything.
  if (state.loading || state.passed < STEPS.length) return null

  const { signed, nextStep } = state
  const step = nextStep && STEPS.find((s) => s.number === nextStep)
  const allSigned = signed >= STEPS.length

  return (
    <Box bg="ink" color="onInk" borderRadius="cardLg" p={{ base: 6, md: 8 }}>
      <HStack spacing={3} mb={4}>
        <Flex boxSize="30px" borderRadius="full" bg="accent.500" align="center" justify="center" flexShrink={0}>
          <Icon as={Award} boxSize={4} />
        </Flex>
        <Text textStyle="eyebrow" color="accent.500">
          {allSigned ? 'Signed off on all thirteen' : 'All thirteen known'}
        </Text>
      </HStack>

      <Text fontSize="display-md" fontWeight={600} letterSpacing="-0.02em" lineHeight={1.15} mb={3}>
        {allSigned
          ? 'You have done the whole thing properly.'
          : 'Knowing it and doing it are two different things.'}
      </Text>

      <Text fontSize="body" color="onInkMuted" lineHeight={1.68} maxW="measure" mb={5}>
        {allSigned
          ? 'Every step passed and every step watched on the floor. The work now is keeping it sharp, which is what the drills are for.'
          : `You have passed the check on all ${STEPS.length} steps, which proves you know the process. ${signed} of ${STEPS.length} have been signed off, which is the part where a manager watches you run it with a real customer. That is the harder half and it is where the process actually gets installed.`}
      </Text>

      {/* Thirteen marks again, but counting what was witnessed rather than what
          was answered. Same shape, harder standard. */}
      <HStack spacing={1.5} mb={6} aria-label={`${signed} of ${STEPS.length} steps signed off`}>
        {Array.from({ length: STEPS.length }, (_, i) => (
          <Box key={i} h="3px" flex={1} borderRadius="full" bg={i < signed ? 'accent.500' : 'onInkFill'} />
        ))}
      </HStack>

      <VStack align="stretch" spacing={2} maxW="380px">
        {!allSigned && step && (
          <Button
            as={RouterLink}
            to={`/courses/roadmap/${step.slug}/`}
            bg="onInk"
            color="ink"
            _hover={{ bg: 'onInkStrong' }}
            rightIcon={<ArrowRight size={16} />}
          >
            Ask to be signed off on Step {step.number}
          </Button>
        )}
        <Button
          as={RouterLink}
          to={`/coach/?practice=${nextStep || 1}`}
          variant="outline"
          borderColor="onInkFill2"
          color="onInk"
          _hover={{ bg: 'onInkFill' }}
        >
          Run a drill instead
        </Button>
      </VStack>
    </Box>
  )
}
